import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import { useCreateNewReservation } from "../../../../redux/flows/keyHandoverCreateReservationFlow";

/**
 * Custom hook to automatically create a reservation when all required data is available.
 * 
 * @remarks
 * - Creates reservation only once per mount
 * - Waits for location and service hydration to complete
 * - Validates required booking data before creation
 * 
 * @param locationHydrated - Whether location hydration is complete
 * @param serviceHydrated - Whether service hydration is complete
 * @returns Object with creating state
 * 
 * @example
 * ```tsx
 * function SummaryPage() {
 *   const locationHydrated = useLocationHydration();
 *   const serviceHydrated = useServiceHydration();
 *   const { creating } = useReservationCreation(locationHydrated, serviceHydrated);
 *   
 *   if (creating) return <Spinner />;
 *   return <Summary />;
 * }
 * ```
 */
export function useReservationCreation(
  locationHydrated: boolean,
  serviceHydrated: boolean
): { creating: boolean } {
  const [creating, setCreating] = useState(false);
  const createdRef = useRef(false);
  const initialReservationsCheckedRef = useRef(false);

  const reservations = useSelector((state: RootState) => state.bookingCreation.reservations);
  const selectedGroup = useSelector((state: RootState) => state.bookingCreation.selectedGroup);
  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);

  const location = useLocation();
  // Get customerAgreementId from URL if present
  const params = new URLSearchParams(location.search);
  const customerAgreementId = params.get("customerAgreementId") || undefined;
  const createNewReservation = useCreateNewReservation("keys", customerAgreementId);

  // Check initial reservations state once
  useEffect(() => {
    if (initialReservationsCheckedRef.current) return;
    
    if (reservations && reservations.length > 0) {
      createdRef.current = true;
    }
    initialReservationsCheckedRef.current = true;
  }, [reservations]);

  // Create reservation only if needed
  useEffect(() => {
    if (!initialReservationsCheckedRef.current) return;
    if (createdRef.current) return;
    if (!locationHydrated || !serviceHydrated) return;

    const createReservationIfNeeded = async () => {
      // Validate required data
      if (!selectedGroup || !orderData.checkInDate || !orderData.checkOutDate) {
        // Mark as created to prevent infinite loop if data never arrives
        createdRef.current = true;
        setCreating(false);
        return;
      }

      try {
        setCreating(true);
        createdRef.current = true; // Set BEFORE creating to prevent double-creation
        await createNewReservation();
      } catch (error) {
        console.error("Failed to create reservation:", error);
        createdRef.current = false; // Reset on error to allow retry
      } finally {
        setCreating(false);
      }
    };

    createReservationIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHydrated, serviceHydrated]);

  return { creating };
}
